import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './auth/auth-context';
import { useOnboarding } from './onboarding-context';

type ReviewContextValue = {
  // ✅ new boot flags
  reviewBootLoading: boolean;
  reviewReady: boolean;

  reviews: any[];

  // ✅ keep the rest of your existing review context value as-is
  // (add your existing fields here)
};

const ReviewContext = createContext<ReviewContextValue | null>(null);

export const useReview = () => {
  const ctx = useContext(ReviewContext);
  if (!ctx) throw new Error('useReview must be used within ReviewProvider');
  return ctx;
};

export const ReviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { sessionLoading, profileReadyForRouting, profile, user, session } = useAuth();
  const { onboardingBootLoading, questions } = useOnboarding();

  const [reviewBootLoading, setReviewBootLoading] = useState(true);

  // ---- KEEP YOUR EXISTING REVIEW LOGIC HERE ----
  // Example:
  // const [someReviewState, setSomeReviewState] = useState(...)

  /**
   * ✅ No review API:
   * We still "boot" deterministically:
   * - If not authed -> review ready immediately
   * - If authed -> wait until auth/profile is routing-ready and onboarding boot is done
   */
  useEffect(() => {
    const authed = !!user || !!session?.access_token;

    if (!authed) {
      setReviewBootLoading(false);
      return;
    }

    if (sessionLoading) return;
    if (!profileReadyForRouting) return;
    if (!profile) return;
    if (onboardingBootLoading) return;

    // if your review context derives data from onboarding/questions,
    // do that work here, then set false.
    setReviewBootLoading(false);
  }, [
    user,
    session,
    sessionLoading,
    profileReadyForRouting,
    profile,
    onboardingBootLoading,
  ]);

  const reviews = useMemo(() => {
    return questions.filter(q => q.is_answered);
  }, [questions]);

  const value = useMemo<ReviewContextValue>(
    () => ({
      reviewBootLoading,
      reviewReady: !reviewBootLoading,
      reviews,
      // ✅ include your existing values here
    }),
    [reviewBootLoading, reviews]
  );

  return <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>;
};
